import { useState, useEffect, useCallback } from 'react'
import api from '../services/api'
import { newIdemKey } from '../lib/platform.js'

/**
 * The one conversation with the ProTrack agent, shared by the desktop panel and the mobile
 * shell.
 *
 * WHY A HOOK AND NOT TWO COMPONENTS. This flow proposes actions that move money — submit a
 * deliverable, amend a bill, confirm an invoice — and every one of them is gated by a
 * single-use confirm token. Two copies of that logic is two places for a double-submit or a
 * skipped confirmation to appear, and only one of them would get fixed. Both surfaces call
 * this; neither owns it.
 *
 * ONE MODE, NOT TWO. There used to be an "advisory" chat and an "agent" chat behind a toggle,
 * and nobody should have to know the difference. Now there is one conversation: it goes to the
 * agent, and falls back to advisory Q&A **only** when the agent is genuinely unavailable —
 * the key is unset, or the server says so. That fallback is deliberately invisible: the point
 * of removing the toggle is that a question still gets answered when the agent cannot act.
 */
export function useAgentChat({ greeting } = {}) {
  const [messages, setMessages] = useState(greeting ? [{ role: 'assistant', content: greeting }] : [])
  const [thinking, setThinking] = useState(false)
  const [files, setFiles] = useState([])
  const [agentAvail, setAgentAvail] = useState(null)   // null = not asked yet
  const [usedTokens, setUsedTokens] = useState(new Set())

  useEffect(() => {
    api.get('/ai/agent/status')
      .then(r => setAgentAvail(!!r.data?.available))
      .catch(() => setAgentAvail(false))
  }, [])

  /** Add picked files, skipping ones already staged — a consultant who opens the picker twice
   *  and re-selects the same receipt must not end up claiming the expense two times. */
  const addFiles = useCallback((picked) => {
    const incoming = Array.from(picked || [])
    if (!incoming.length) return
    setFiles(prev => {
      const seen = new Set(prev.map(f => `${f.name}:${f.size}`))
      return [...prev, ...incoming.filter(f => !seen.has(`${f.name}:${f.size}`))]
    })
  }, [])

  const removeFile = useCallback((i) => setFiles(prev => prev.filter((_, j) => j !== i)), [])

  const say = (msg) => setMessages(prev => [...prev, msg])

  /** Advisory Q&A — the fallback, never chosen by the user. */
  const askAdvisory = async (content, history) => {
    try {
      const r = await api.post('/ai/chat', { message: content, history })
      say({ role: 'assistant', content: r.data?.answer || '…', links: r.data?.links || [] })
    } catch (e) {
      say({ role: 'assistant', content: e.response?.data?.detail || 'Sorry, something went wrong. Try again.', links: [] })
    }
  }

  const send = useCallback(async (raw) => {
    const content = (raw || '').trim()
    if (thinking) return
    if (!content && files.length === 0) return

    const history = messages.filter(m => !m.proposal && !m.form).map(m => ({ role: m.role, content: m.content }))
    const attached = files
    say({ role: 'user', content: content + (attached.length ? `  📎 ${attached.length} file(s)` : '') })
    setFiles([])
    setThinking(true)

    // Known-unavailable: go straight to advisory rather than making the person wait for a
    // round trip that will only tell them the same thing.
    if (agentAvail === false) {
      await askAdvisory(content, history)
      setThinking(false)
      return
    }

    try {
      const fd = new FormData()
      fd.append('message', content)
      fd.append('history', JSON.stringify(history))
      attached.forEach(f => fd.append('attachments', f))
      const r = await api.post('/ai/agent', fd, { headers: { 'Content-Type': 'multipart/form-data' } })
      const d = r.data || {}

      if (d.available === false) {
        // The server just told us the agent is off. Remember it, and answer the question
        // anyway instead of reporting a failure the person cannot do anything about.
        setAgentAvail(false)
        await askAdvisory(content, history)
      } else if (d.needs_confirm && d.proposal) {
        say({ role: 'assistant', content: d.answer || 'Please review and confirm:', proposal: d.proposal })
      } else if (d.form) {
        say({ role: 'assistant', content: d.answer || 'Please fill this in:', form: d.form })
      } else if (d.error) {
        say({ role: 'assistant', content: `⚠️ ${d.error}` })
      } else {
        say({
          role: 'assistant',
          content: d.answer || 'Done.',
          links: d.result?.link ? [{ href: d.result.link, label: 'Open' }] : [],
        })
      }
    } catch (e) {
      say({ role: 'assistant', content: e.response?.data?.detail || 'The agent hit an error. Please try again.' })
    } finally {
      setThinking(false)
    }
  }, [thinking, files, messages, agentAvail])

  /** Press Confirm on a proposal. The token is single-use and burned BEFORE the request, so a
   *  double tap cannot submit the same deliverable twice. */
  const confirmProposal = useCallback(async (proposal) => {
    if (usedTokens.has(proposal.confirm_token) || thinking) return
    setUsedTokens(prev => new Set(prev).add(proposal.confirm_token))
    setThinking(true)
    try {
      const fd = new FormData()
      fd.append('message', '')
      fd.append('confirm_token', proposal.confirm_token)
      fd.append('idempotency_key', newIdemKey())
      const r = await api.post('/ai/agent', fd, { headers: { 'Content-Type': 'multipart/form-data' } })
      const d = r.data || {}
      say({ role: 'assistant', content: d.error ? `⚠️ ${d.error}` : (d.answer || 'Done.') })
    } catch (e) {
      say({ role: 'assistant', content: e.response?.data?.detail || 'Could not complete the action.' })
    } finally {
      setThinking(false)
    }
  }, [usedTokens, thinking])

  const cancelProposal = useCallback((proposal) => {
    setUsedTokens(prev => new Set(prev).add(proposal.confirm_token))
    say({ role: 'assistant', content: 'Okay, cancelled — nothing was changed.' })
  }, [])

  const isUsed = useCallback((token) => usedTokens.has(token), [usedTokens])

  return {
    messages, setMessages, thinking, files,
    addFiles, removeFile,
    send, confirmProposal, cancelProposal, isUsed,
    agentAvail,
  }
}

export default useAgentChat
